<template>
  <section
    v-if="percent !== null"
    class="ex-comm"
    :aria-label="t('exclusiveUnits.commission.title')"
  >
    <div class="head">
      <p class="lbl">{{ t('exclusiveUnits.commission.title') }}</p>
      <span class="chip">{{ t('exclusiveUnits.commission.splitChip', { share: detail.agentSharePercent }) }}</span>
    </div>
    <dl class="rows">
      <div class="row">
        <dt>{{ t('exclusiveUnits.commission.developerCommission') }}</dt>
        <dd>{{ formatPercent(percent) }}</dd>
      </div>
      <div class="row">
        <dt>{{ t('exclusiveUnits.commission.yourSplit') }}</dt>
        <dd>{{ formatPercent(detail.agentSharePercent) }}</dd>
      </div>
      <div class="row net">
        <dt>{{ t('exclusiveUnits.commission.netCommission') }}</dt>
        <dd>{{ formatPercent(netPercent) }}</dd>
      </div>
      <div
        v-if="earnings"
        class="row earnings"
      >
        <dt>{{ t('exclusiveUnits.commission.estEarnings') }}</dt>
        <dd>{{ earnings }}</dd>
      </div>
    </dl>
    <p
      v-if="payoutLine"
      class="payout"
    >
      {{ payoutLine }}
    </p>
  </section>
</template>

<script setup lang="ts">
import type { ExclusiveDetail } from '~/types/exclusives';
import { useAedAmount } from '~/composables/useAedAmount';

// Selected unit earnings (from the live units list) replace the project "from" amount.
const { detail, selectedEarningsAed = null } = defineProps<{
  detail: ExclusiveDetail;
  selectedEarningsAed?: number | null;
}>();

const { t } = useI18n();
const { formatAed } = useAedAmount();

// Hidden until the admin sets the commission %.
const percent = computed(() => detail.agentCommissionPercent);
const netPercent = computed(() => ((percent.value ?? 0) * detail.agentSharePercent) / 100);

const formatPercent = (value: number) => `${Math.round(value * 100) / 100}%`;

const earnings = computed(() => {
  if (selectedEarningsAed !== null) return formatAed(selectedEarningsAed);
  if (detail.fromAgentEarningsAed === null) return null;
  return t('exclusiveUnits.commission.estEarningsFrom', { amount: formatAed(detail.fromAgentEarningsAed) });
});

// No payout-time promise when FastPay is off for the project.
const payoutLine = computed(() => {
  if (!detail.fastPayAvailable) return null;
  const hours = detail.payoutSlaHours;
  return detail.fastPayDeductionPoints > 0
    ? t('exclusiveUnits.commission.payoutFastPay', { hours, points: detail.fastPayDeductionPoints })
    : t('exclusiveUnits.commission.payoutNoDeduction', { hours });
});
</script>

<style scoped lang="scss">
.ex-comm {
  background: #c6ffc4;
  border-radius: 16px;
  padding: 20px 22px;
  border: 1px solid rgba(34, 197, 94, 0.3);
  color: #0d0f15;

  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin: 0 0 12px;
  }

  .lbl {
    font-size: 15px;
    font-weight: 700;
    margin: 0;
  }

  .chip {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    background: #fff;
    border-radius: 6px;
    padding: 4px 8px;
    white-space: nowrap;
  }

  .rows {
    margin: 0;
  }

  .row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 14px;
    padding: 4px 0;

    dt {
      color: rgba(13, 15, 21, 0.7);
    }

    dd {
      margin: 0;
      font-weight: 700;
    }
  }

  .net {
    border-top: 1px solid rgba(13, 15, 21, 0.12);
    margin-top: 8px;
    padding-top: 12px;
    font-size: 16px;

    dt {
      color: #0d0f15;
      font-weight: 700;
    }

    dd {
      font-size: 22px;
    }
  }

  .earnings dd {
    font-size: 18px;
  }

  .payout {
    font-size: 13px;
    font-weight: 600;
    color: #15803d;
    margin: 12px 0 0;
  }
}
</style>
